import type {
  FocusEvent,
  FocusEventHandler,
  KeyboardEventHandler,
  RefObject
} from 'react';
import { useEffect, useRef } from 'react';
import { uuid } from '@elfsight/lib-utils';
import type { TextControlBaseInstance } from '@elfsight/widget-ui';
import type { DropdownOption } from '../text-dropdown';
import { useDropdownNavigation } from './useDropdownNavigation';

interface UseDropdownHandlersOptions {
  disableDropdown: boolean;
  actualInputType: string;
  shouldDisableOnChange: boolean;
  localValue: string;
  options: DropdownOption[];
  open: boolean;
  anchorElementRef: RefObject<HTMLDivElement>;
  iconRef: RefObject<HTMLDivElement>;
  inputControlRef: RefObject<TextControlBaseInstance | null>;
  setOpen: (open: boolean) => void;
  setFocused: (focused: boolean) => void;
  setDisplayedValue: (value: string) => void;
  setLocalValue: (value: string) => void;
  parseInputValue?: (inputValue: string) => string;
  validateInput?: (inputValue: string) => boolean;
  formatValue?: (value: string) => string;
  nearestOption?: DropdownOption | null;
  onChange?: (value: string) => void;
  onOptionSelect?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

interface UseDropdownHandlersType {
  handleOpen: () => void;
  handleClose: () => void;
  handleDropdownCloseRequest: (e?: PointerEvent) => void;
  handleFocus: () => void;
  handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleKeyDown: KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  handleChange: (newValue: string) => void;
  handleSelectOption: (optionValue: string) => void;
  highlightedIndex: number;
}

export function useDropdownHandlers({
  disableDropdown,
  actualInputType,
  shouldDisableOnChange,
  localValue,
  options,
  open,
  anchorElementRef,
  iconRef,
  inputControlRef,
  setOpen,
  setFocused,
  setDisplayedValue,
  setLocalValue,
  parseInputValue,
  validateInput,
  formatValue,
  nearestOption,
  onChange,
  onOptionSelect,
  onFocus,
  onBlur
}: UseDropdownHandlersOptions): UseDropdownHandlersType {
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTimeField = actualInputType === 'time';

  const clearFocusTimeout = () => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
  };

  const createOptionFromValue = (value: string): DropdownOption => ({
    id: uuid(),
    value,
    label: formatValue ? formatValue(value) : value
  });

  const applyValue = (option: DropdownOption) => {
    const displayValue = shouldDisableOnChange ? option.value : option.label;

    setLocalValue(option.value);
    setDisplayedValue(displayValue);
    onChange?.(displayValue);
  };

  const handleSelectOption = (optionValue: string) => {
    const selectedOption =
      options.find((option) => option.value === optionValue) ??
      createOptionFromValue(optionValue);

    applyValue(selectedOption);
    onOptionSelect?.(optionValue);
    handleClose();
  };

  const { highlightedIndex, handleKeyDown } = useDropdownNavigation({
    open,
    options,
    localValue,
    nearestOption,
    parseInputValue,
    onNavigate: applyValue,
    onSelect: handleSelectOption,
    inputControlRef
  });

  const handleOpen = () => {
    setFocused(true);

    if (disableDropdown) {
      return;
    }

    setOpen(true);

    requestAnimationFrame(() => {
      inputControlRef.current?.inputRef.current?.focus();
    });
  };

  const handleClose = () => {
    setFocused(false);
    setOpen(false);
  };

  const handleDropdownCloseRequest = (e?: PointerEvent) => {
    const anchor = anchorElementRef.current;
    const icon = iconRef.current;
    const target = e?.target;

    if (
      target instanceof Node &&
      anchor?.contains(target) &&
      !icon?.contains(target)
    ) {
      return;
    }

    handleClose();
  };

  const handleBlur: FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    clearFocusTimeout();

    const parsedValue = parseInputValue
      ? parseInputValue(localValue)
      : localValue;

    const isValidValue = !validateInput || validateInput(parsedValue);
    const fallbackValue = nearestOption?.value ?? parsedValue;
    const finalValue = isValidValue ? parsedValue : fallbackValue;

    const finalOption =
      options.find((opt) => opt.value === finalValue) ??
      createOptionFromValue(finalValue);
    applyValue(finalOption);

    onBlur?.(e);

    setTimeout(() => {
      handleClose();
    }, 150);
  };

  const handleFocus = () => {
    onFocus?.();

    if (focusTimeoutRef.current !== null) {
      return;
    }

    focusTimeoutRef.current = setTimeout(() => {
      handleOpen();
      focusTimeoutRef.current = null;
    }, 150);
  };

  const handleChange = (newValue: string) => {
    if (shouldDisableOnChange) {
      return;
    }

    setDisplayedValue(newValue);
    setLocalValue(newValue);

    if (!isTimeField) {
      onChange?.(newValue);
    }

    if (!disableDropdown) {
      setOpen(true);
      setFocused(true);
    }
  };

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  return {
    handleOpen,
    handleClose,
    handleDropdownCloseRequest,
    handleFocus,
    handleBlur,
    handleKeyDown,
    handleChange,
    handleSelectOption,
    highlightedIndex
  };
}
